import { inject, Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, GuardResult, MaybeAsync, Router } from '@angular/router';
import { AuthenticationService } from '@flexyoo/auth';
import { EmployeeSettingsService } from '@flexyoo/employee/data-access';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { Store } from '@ngrx/store';
import { map, of, switchMap, take } from 'rxjs';
import { appRoutesPerRoleDefaults } from 'src/app/app-routes-per-role-defaults.constant';
import { RoleEnum } from 'src/app/shared/models/general/general-enum';

@Injectable({ providedIn: 'root' })
export class HrCafeteriaPlanGuard implements CanActivate, CanActivateChild {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthenticationService);
  private readonly employeeSettingsService = inject(EmployeeSettingsService);
  private readonly store = inject(Store);

  canActivate(): MaybeAsync<GuardResult> {
    return this.checkAccess();
  }

  canActivateChild(): MaybeAsync<GuardResult> {
    return this.checkAccess();
  }

  private checkAccess(): MaybeAsync<GuardResult> {
    const user = this.auth.getCurrentUser();

    if (!user) {
      this.auth.logout();
      return of(false);
    }

    if (user.Role === RoleEnum.HR) {
      return this.employeeSettingsService.updateAndLoadTenantSettings(user.Id).pipe(
        switchMap(() =>
          this.store
            .select(tenantSettingsSelectors.selectIsCafeteriaEnabledForAnyTenant)
            .pipe(take(1))
        ),
        map((isCafeteriaEnabled) => {
          if (isCafeteriaEnabled) {
            return true;
          }

          void this.router.navigate(['/hr/dashboard']);
          return false;
        })
      );
    }

    if (user.Role === RoleEnum.Employee) {
      this.auth.navigateToEmployeeDefaultRoute();
      return false;
    }

    if ([RoleEnum.Admin, RoleEnum.Opco].includes(user.Role as RoleEnum)) {
      return this.router.navigate([appRoutesPerRoleDefaults[user.Role]]);
    }

    this.auth.logout();
    return false;
  }
}
